import { z } from 'zod';
import { DateTimeSchema } from './schemas/dates.js';
import * as R from 'remeda';
import { ObjectId } from './libs/bson.js';
import { BullhornApiField } from './schemas/external/bullhornSchemas/index.js';
import { ObjectIdSchema, ObjectIdString } from './schemas/common.js';
import { PersonSubType } from './schemas/external/bullhornSchemas/bullhornApiEntities.js';

export { ObjectId, EJSON } from './libs/bson.js';

export * from './schemas/objectId.js';

export type NamedType<T> = Pick<T, keyof T>;

export interface ObjectOf<T> {
  [key: string]: T;
}

export type Falsy = false | null | undefined | void | '' | 0;

/** @deprecated - Use UnknownObject instead */
export type AnyObject = ObjectOf<any>;

export type NestedObjectOf<T extends string | number | boolean | undefined | null> = ObjectOf<
  T | NestedObjectOf<T>
>;

// Helper type to set some fields to required
export type WithRequired<T extends object, Keys extends keyof T> = T & Required<Pick<T, Keys>>;

// Helper type to set some fields to optional
export type WithOptional<T extends object, Keys extends keyof T> = Omit<T, Keys> &
  Partial<Pick<T, Keys>>;

type KeysOfTypeLoose<T, Filter> = {
  [Key in keyof T]: T[Key] extends Filter ? Key : never;
}[keyof T];

type KeysOfTypeReverse<T, Filter> = {
  [Key in keyof T]: Filter extends T[Key] ? Key : never;
}[keyof T];

type KeysOfTypeStrict<T, Filter> = KeysOfTypeLoose<T, Filter> & KeysOfTypeReverse<T, Filter>;

/**
 * Returns an interface stripped of all keys that don't resolve to Filter.
 * Defaults to a non-strict comparison of T[Key] extends Filter.
 * Setting Mode to 'strict' performs a strict 2-way type comparison
 * Setting Mode to 'reverse' performs a comparison of Filter extends T[Key]
 * https://stackoverflow.com/questions/46583883/typescript-pick-properties-with-a-defined-type
 */
export type KeysOfType<T, Filter, Mode extends 'loose' | 'strict' | 'reverse' = 'loose'> =
  Mode extends 'strict' ? KeysOfTypeStrict<T, Filter>
  : Mode extends 'reverse' ? KeysOfTypeReverse<T, Filter>
  : Mode extends 'loose' ? KeysOfTypeLoose<T, Filter>
  : never;

export type PickByType<T, Filter, Mode extends 'loose' | 'strict' | 'reverse' = 'loose'> = Pick<
  T,
  KeysOfType<T, Filter, Mode>
>;

export type FilteredKeyOf<Source, FilterType> = {
  [K in keyof Source]: Source[K] extends FilterType ? K : never;
}[keyof Source] &
  string;

export type StringKeyOf<T> = FilteredKeyOf<T, string>;
export type NumericalKeyOf<T> = FilteredKeyOf<T, number>;

declare const __brand: unique symbol;
type Brand<B> = { [__brand]: B };
export type Branded<T, B> = T & Brand<B>;

//--------------------------------------------------------------------------------------------------------------------//
//region NLP Types

export const NlpQuestionDetectionOutput = z.object({
  hasQuestion: z.boolean(),
  questions: z.string().array().nullable().optional(),
});
export type NlpQuestionDetectionOutput = z.infer<typeof NlpQuestionDetectionOutput>;

export const NlpEntityDetectionOutput = z.object({
  dates: z.string().array().optional(),
  money: z.string().array().optional(),
  places: z.string().array().optional(),
  hospitals: z.string().array().optional(),
  specialties: z.string().array().optional(),
  shifts: z.string().array().optional(),
  competitorCompanies: z.string().array().optional(),
  competitorPhrases: z.string().array().optional(),
  hasCompetitorMention: z.boolean().optional(),
  injuryTerms: z.string().array().optional(),
  safetyTerms: z.string().array().optional(),
  hasWorkersCompRisk: z.boolean().optional(),
});
export type NlpEntityDetectionOutput = z.infer<typeof NlpEntityDetectionOutput>;
// other props will be added as we add more NLP processes
export const NlpData = z.object({
  ...NlpQuestionDetectionOutput.shape,
  ...NlpEntityDetectionOutput.shape,
  metadata: z.object({
    inputHash: z.string(),
    nlpDataVersion: z.string(),
  }),
});
export type NlpData = NlpQuestionDetectionOutput &
  NlpEntityDetectionOutput & {
    metadata: {
      inputHash: string;
      nlpDataVersion: string;
    };
  };

//endregion
//--------------------------------------------------------------------------------------------------------------------//
//region ActivityData Types
export const DateRange = z.object({
  oldest: DateTimeSchema,
  newest: DateTimeSchema,
});

export const RelatedEntityMetadata = z
  .object({
    dateLastFetched: DateTimeSchema,
    lastAttemptedFetch: DateTimeSchema,
    oldestDateFetched: DateTimeSchema,
  })
  .partial();
export type RelatedEntityMetadata = z.infer<typeof RelatedEntityMetadata>;

export const RelatedEntityMetadataMap = z.map(z.string(), RelatedEntityMetadata);
export type RelatedEntityMetadataMap = z.infer<typeof RelatedEntityMetadataMap>;

export const RelatedEntityMetadataObject = z.record(z.string(), RelatedEntityMetadata);
export type RelatedEntityMetadataObject = z.infer<typeof RelatedEntityMetadataObject>;

export const AttachmentInfo = z.object({
  url: z.string(),
  name: z.string(),
});
export type AttachmentInfo = z.infer<typeof AttachmentInfo>;

export const ActivityAuthor = z
  .looseObject({
    id: z.number().or(z.string()),
    name: z.string().nullish(),
    firstName: z.string(),
    lastName: z.string(),
    _subtype: PersonSubType.nullish(),
  })
  .or(z.string());
export type ActivityAuthor = z.infer<typeof ActivityAuthor>;

// A single message parsed out of a rolled-up note
export const SubActivity = z.looseObject({
  id: z.string(),
  direction: z.enum(['inbound', 'outbound', 'system']),
  author: z.string().nullish().catch(null),
  timestamp: DateTimeSchema.nullish().catch(null),
  content: z.string().nullish().catch(null),
  nlpData: NlpData.nullish().catch(null),
});
export type SubActivity = z.infer<typeof SubActivity>;

export const SubActivityFormatName = z.enum(['sms-thread', 'chatbot-thread']);
export type SubActivityFormatName = z.infer<typeof SubActivityFormatName>;

// Container pairing a parser format name with its emitted sub-activities
export const SubActivityThread = z.looseObject({
  format: SubActivityFormatName,
  items: SubActivity.array(),
});
export type SubActivityThread = z.infer<typeof SubActivityThread>;

export const CandidateActivity = z.looseObject({
  _id: ObjectIdString.or(ObjectIdSchema),
  type: z.string(),
  timestamp: DateTimeSchema,
  content: z.string().nullish().catch(null),

  // Sub-activities parsed from a rolled-up wrapper note, paired with their parser format
  subActivityThreadFormat: SubActivityFormatName.nullish(),
  subActivityThread: SubActivityThread.nullish().catch(null),

  startDate: DateTimeSchema.nullish(),
  endDate: DateTimeSchema.nullish(),
  comments: z.string().nullish().catch(null),
  submissionId: z.string().nullish().catch(null),
  category: z.string().nullish().catch(null),
  subject: z.string().nullish().catch(null),
  reason: z.string().nullish().catch(null),
  classification: z.string().nullish().catch(null),
  direction: z.string().nullish().catch(null),
  source: z.string().nullish().catch(null),
  candidateSourceId: z.string().nullish().catch(null),

  isCandidateEngagement: z.boolean().nullish().catch(null),
  enableInboundQuestionDetection: z.boolean().nullish().catch(null),

  entityId: z.string().nullish().catch(null),
  entityType: z.string().nullish().catch(null),
  oldValue: z.string().nullish().catch(null),
  newValue: z.string().nullish().catch(null),

  previousInboundEmail: z
    .object({
      content: z.string(),
      // TODO: check if this should be string
      timestamp: DateTimeSchema.nullish(),
    })
    .nullish()
    .catch(null),
  attachments: AttachmentInfo.array().nullish().catch(null),

  _nlpData: NlpData.nullish().catch(null),

  // Fields copied from frontend interface
  _type: z.string().nullish().catch(null),
  author: ActivityAuthor.nullish().catch(null),
  recipient: z.number().nullish().catch(null),
  billRate: z.number().nullish().catch(null),
  currentStatus: z.string().nullish().catch(null),
  isSourceActivity: z.boolean().nullish().catch(null),
  _isInbound: z.boolean().nullish().catch(null),
  isOutbound: z.boolean().nullish().catch(null),
  _isOutbound: z.boolean().nullish().catch(null),
  _category: z.string().nullish().catch(null),
  jobTitle: z.string().nullish().catch(null),
  entitySourceId: z.string().nullish().catch(null),
  sourceId: z.string().nullish().catch(null),
  icon: z.string().nullish().catch(null),
  detail: z.string().nullish().catch(null),

  // Internal fields
  _regexToRemove: z.string().nullish().catch(null),
  _filteredOut: z.boolean().nullish().catch(null),
});
export type CandidateActivity = z.infer<typeof CandidateActivity>;

export const EntityMetadata = z
  .object({
    count: z.number(),
    omitted: z.number().optional(),
    total: z.number().optional(),
  })
  .default({ count: 0 });
export type EntityMetadata = z.infer<typeof EntityMetadata>;

// TODO: stop exporting this and replace with zodToMongooseSchema
export const activityEntityMetadataFieldList = [
  'notes',
  'submissions',
  'placements',
  'positions',
  'textMessages',
  'emails',
  'tasks',
  'appointments',
  'candidateReferences',
  'inboundActivities',
  'allActivities',
  'candidateEngagement',
] as const;

export const ActivityMetadata = z.object({
  dateRange: DateRange.nullish(),
  candidateRelatedEntities: RelatedEntityMetadataObject.default({}),
  ...R.fromKeys(activityEntityMetadataFieldList, () => EntityMetadata),
});
export type ActivityMetadata = z.infer<typeof ActivityMetadata>;

export const TokenLimitingCalculationMetadata = z.object({
  initialPercentToRemove: z.number().describe('The initial uncapped removal percentage calculated'),
  iterations: z.number().describe('Number of iterations needed to get under the limit'),
});
export type TokenLimitingCalculationMetadata = z.infer<typeof TokenLimitingCalculationMetadata>;

export const TokenLimitingMetadata = z
  .object({
    applied: z.boolean().describe('Whether token limiting was applied'),
    modelLimit: z.number().describe('The token limit for the given model'),
    estimatedOriginalTokens: z.number().describe('Estimated token count of the original request'),
    estimatedFinalTokenCount: z.number().describe('Estimated token count after trimming'),
    calculationMetadata: TokenLimitingCalculationMetadata.partial()
      .nullish()
      .describe('Calculation metadata for testing'),

    // candidate activity token limiting properties
    notesRemoved: z.number().nullish().describe('Number of notes/events removed by trimming'),
    dataRemoved: z.number().nullish().describe('Number of data items removed by trimming'),
    dateRangeReduced: z
      .boolean()
      .nullish()
      .describe('Whether the effective date range was reduced due to trimming'),
    originalDateRange: DateRange.partial().nullish().describe('Original date range of activities'),
    actualDateRange: DateRange.partial()
      .nullish()
      .describe('Actual date range of activities after trimming'),
  })
  .partial();
export type TokenLimitingMetadata = z.infer<typeof TokenLimitingMetadata>;

export const AnalyticsTokenLimitingMetadata = TokenLimitingMetadata.extend({
  // analytics opportunity token limiting properties
  opportunitiesRemoved: z.number().describe('Number of opportunities removed by random sampling'),
  originalOpportunityCount: z.number().describe('Original opportunity count'),
  finalOpportunityCount: z.number().describe('Final opportunity count after sampling'),
  samplingMethod: z.string().describe('Sampling method used'),
});
export type AnalyticsTokenLimitingMetadata = z.infer<typeof AnalyticsTokenLimitingMetadata>;

// Why a request asking for flex processing was re-sent at standard pricing instead
export const ServiceTierFallbackReason = z.enum([
  'timeout',
  'rate_limited',
  'transient_error',
  'unsupported_tier',
]);
export type ServiceTierFallbackReason = z.infer<typeof ServiceTierFallbackReason>;

export const LlmInputMetadata = z.object({
  ...ActivityMetadata.shape,
  tokenLimiting: TokenLimitingMetadata.nullish(),
  chat: z.object({
    lastMessage: ObjectIdSchema.optional(),
  }),
  // Marks a Prompt Lab test run; maps the response onto the test statuses when it's saved
  isTest: z.boolean().optional(),
  customMetadata: z.looseObject({}).optional(),
  serviceTierFallback: ServiceTierFallbackReason.nullish(),
});
export type LlmInputMetadata = z.infer<typeof LlmInputMetadata>;

export type EntityWithJobOrder = {
  rawData?: {
    jobOrder?: {
      title?: string;
    };
  };
};

export const activityTypes = [
  'Note',
  'TextMessage',
  'EditHistory',
  'Position',
  'Submission',
  'Placement',
  'AtsEmail',
  'Task',
  'Appointment',
  'CandidateReference',
] as const;
export const ActivityType = z.enum(activityTypes);
export type ActivityType = (typeof activityTypes)[number];

export const ActivityCounts = z.partialRecord(z.enum([...activityTypes, 'total']), z.number());
export type ActivityCounts = z.infer<typeof ActivityCounts>;

export const ActivityPerson = z.looseObject({
  id: z.string(),
  name: z.string(),
  firstName: z.string().nullish(),
  lastName: z.string().nullish(),
  subType: PersonSubType,
  activityCounts: ActivityCounts,

  // CorporateUser-specific fields
  occupation: z.string().nullish(),
  primaryDepartment: z.string().nullish(),
  userType: z.string().nullish(),
});
export type ActivityPerson = z.infer<typeof ActivityPerson>;

export const ActivityData = z.object({
  events: CandidateActivity.array(),
  data: CandidateActivity.array(),
  people: ActivityPerson.array().optional(),
});
export type ActivityData = z.infer<typeof ActivityData>;

//endregion
//--------------------------------------------------------------------------------------------------------------------//

// Bullhorn API field metadata
export type BullhornField = BullhornApiField;
